import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { traverse } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { renderDocument } from '@atlaskit/renderer/render-document';
import { TextSerializer } from '@atlaskit/renderer/text-serializer';
enum MediaType {
	Image = 'image',
	Video = 'video',
	Link = 'link',
	Other = 'other',
}

const mediaPlaceholderFallback = '[media]';

const textSerializer = new TextSerializer(defaultSchema);

const getMediaTypeFromNode = (node: ADFEntity): MediaType => {
	let mimeType = node.attrs?.__fileMimeType || '';
	if (mimeType === 'image/gif') {
		return MediaType.Video;
	}
	if (mimeType.startsWith('image/')) {
		return MediaType.Image;
	}
	if (mimeType.startsWith('video/')) {
		return MediaType.Video;
	}
	if (isTextWithLink(node)) {
		return MediaType.Link;
	}
	return MediaType.Other;
};

const parseADF = (
	adfString: string,
	attachmentsRenderStyle:
		| 'inlinePlainTextPlaceholder'
		| 'attachmentsFooter'
		| 'attachmentsFooterWithMediaHydration' = 'inlinePlainTextPlaceholder',
): ADFEntity => {
	const adf = JSON.parse(adfString);
	return traverseAndRemoveMediaRelatedNodes(adf, attachmentsRenderStyle);
};

const convertToPlaceholder = (node: ADFEntity, replaceMediaNodesWith: string) => {
	if (isSmartLink(node)) {
		node.text = node.attrs?.url || node.attrs?.data || replaceMediaNodesWith;
		if (node.attrs?.url) {
			node.marks = [
				{
					type: 'link',
					attrs: {
						href: node.attrs?.url,
					},
				},
			];
			delete node.attrs;
		}
	} else {
		node.text = replaceMediaNodesWith;
	}
	node.type = 'text';
	node.content = [];
	return node;
};

const isMediaRelatedNode = (node: ADFEntity, includeSmartLinks: boolean = true): boolean => {
	const isMedia = ['media', 'mediaSingle', 'mediaGroup'].includes(node.type);
	const isInlineMediaImage =
		node.type === 'inlineExtension' && node.attrs?.extensionKey === 'inline-media-image';

	const isLinkText = includeSmartLinks && isTextWithLink(node);
	const isSmartLinkNode = includeSmartLinks && isSmartLink(node);
	return isMedia || isInlineMediaImage || isLinkText || isSmartLinkNode;
};

const isMediaNode = (node: ADFEntity): boolean => {
	return (
		'media' === node.type ||
		(node.type === 'inlineExtension' && node.attrs?.extensionKey === 'inline-media-image')
	);
};
const isSmartLink = (node: ADFEntity): boolean => {
	const isSmartLinkType = ['inlineCard', 'blockCard', 'embedCard'].includes(node.type);
	const hasURL = node.attrs?.url !== undefined;
	return isSmartLinkType && hasURL;
};

const isTextWithLink = (node: ADFEntity): boolean => {
	const linkMark = node.marks?.find((mark) => mark.type === 'link');
	return node.type === 'text' && linkMark !== undefined && node.text !== linkMark.attrs?.href;
};

const isMediaOrEmptyParagraph = (node: ADFEntity | undefined): boolean => {
	if (!node) {
		return false;
	}

	if (node.type === 'paragraph') {
		return (
			!node.content ||
			node.content.length === 0 ||
			node.content.every((n) => n && isMediaRelatedNode(n, false))
		);
	}

	return isMediaRelatedNode(node, false);
};

const renderStyleisUsesAttachmentsFooter = (
	attachmentsRenderStyle:
		| 'inlinePlainTextPlaceholder'
		| 'attachmentsFooter'
		| 'attachmentsFooterWithMediaHydration',
): boolean => {
	return (
		attachmentsRenderStyle === 'attachmentsFooter' ||
		attachmentsRenderStyle === 'attachmentsFooterWithMediaHydration'
	);
};

const traverseAndRemoveMediaRelatedNodes = (
	adfEntity: ADFEntity,
	attachmentsRenderStyle:
		| 'inlinePlainTextPlaceholder'
		| 'attachmentsFooter'
		| 'attachmentsFooterWithMediaHydration' = 'inlinePlainTextPlaceholder',
): ADFEntity => {
	let removeNode = false;

	const replaceMediaNodesWith = renderStyleisUsesAttachmentsFooter(attachmentsRenderStyle)
		? '...'
		: '[media]';

	return (
		traverse(adfEntity, {
			any: (node, _) => {
				if (isTextWithLink(node)) {
					return node;
				}

				// If type of this node is doc (root node) and the content of this node is only mediaRelated nodes, then remove it/return false
				if (
					node.type === 'doc' &&
					node.content &&
					node.content.every(isMediaOrEmptyParagraph) &&
					renderStyleisUsesAttachmentsFooter(attachmentsRenderStyle)
				) {
					node.content = [
						{
							type: 'paragraph',
							content: [],
						},
					];
					return node;
				}

				if (isMediaRelatedNode(node)) {
					if (!removeNode || isSmartLink(node)) {
						return convertToPlaceholder(node, replaceMediaNodesWith);
					}
					return false;
				}
			},
		}) || adfEntity
	);
};

const getMediaNodesFromAdfDocument = (adfString: string) => {
	const adf = JSON.parse(adfString);
	const mediaNodes: ADFEntity[] = [];

	const collectMediaNodes = (node: ADFEntity | undefined) => {
		if (node && (isMediaNode(node) || isTextWithLink(node))) {
			mediaNodes.push(node);
		}
		node?.content?.forEach(collectMediaNodes);
	};

	collectMediaNodes(adf);
	return mediaNodes;
};

const getMediaNameFromMediaNode = (mediaNode: ADFEntity) => {
	const mediaType = getMediaTypeFromNode(mediaNode);
	let mediaName;

	if (mediaType === MediaType.Link) {
		const linkMark = mediaNode.marks?.find((mark) => mark.type === 'link');
		mediaName = linkMark?.attrs?.href;
	} else {
		if (mediaNode.attrs?.__fileName) {
			mediaName = mediaNode.attrs?.__fileName;
		} else if (mediaNode.attrs?.alt) {
			mediaName = mediaNode.attrs?.alt;
		}
	}

	return mediaName || mediaPlaceholderFallback;
};

const getPlainTextFromAdfDocument = (
	adf: string,
	attachmentsRenderStyle:
		| 'inlinePlainTextPlaceholder'
		| 'attachmentsFooter'
		| 'attachmentsFooterWithMediaHydration' = 'inlinePlainTextPlaceholder',
): string => {
	const parsedAdf = parseADF(adf, attachmentsRenderStyle);
	return renderDocument(parsedAdf, textSerializer).result ?? '';
};

export {
	parseADF,
	getPlainTextFromAdfDocument,
	getMediaNodesFromAdfDocument,
	getMediaNameFromMediaNode,
	getMediaTypeFromNode,
	MediaType,
};
